import { RefObject, useMemo } from 'react'
import { useDispatch } from 'react-redux'
import {
  addMultiPostComment,
  editMultiPosts,
  removeMultiPosts,
  setMultiPostFBAlbums,
  setMultiPostFBReshareAll,
  setMultiPostAiContent,
  setMultiPostIGLocations,
  setMultiPostPiBoards,
  setMultiPostTweetTypeAll,
  setMultiPostsRecycle,
  setMultipostTikTokProfileSettings
} from 'services/compose'
import { BRAND_TIKTOK, BRAND_YOUTUBE, PrivacyStatus, TikTokPostProfileSettings } from 'shared'
import { BRAND_FACEBOOK, BRAND_INSTAGRAM, BRAND_PINTEREST, BRAND_TWITTER } from 'shared/constants'
import {
  ComposerFacebookPost,
  ComposerPinterestPost,
  ComposerTwitterPost,
  FBAlbum,
  LocationInfo,
  PinterestAiContentMode,
  PinterestBoard,
  PostDestinationType,
  TweetType
} from 'shared/types'
import { StoreThunkDispatch } from 'store/state'
import { moveIdToBottom, moveIdToTop } from '../postOrder'

export interface BulkPostHandlers {
  onMoveToTop: (id: string) => void
  onMoveToBottom: (id: string) => void
  onRemove: (id: string) => void
  onToggleSelected: (id: string) => void
  onSelectedFBAlbumsChange: (postId: string, albums: Record<string, FBAlbum | undefined>, applyToAll: boolean) => void
  onSelectedPIBoardsChange: (postId: string, selection: Record<string, PinterestBoard>, applyToAll?: boolean) => void
  onSelectedIGLocationsChange: (postId: string, locations: Record<string, LocationInfo | undefined>, applyToAll: boolean) => void
  onStatusChange: (postId: string, text: string, html: string, network: PostDestinationType) => void
  onRecycleChange: (postId: string, value: number | boolean | Date, network: PostDestinationType, applyToAll?: boolean) => void
  onPinChange: (postId: string, data: { title?: string, description?: string, destinationUrl?: string, applyToAll?: boolean }) => void
  onYtStatusChange: (postId: string, data: { title?: string, description?: string }) => void
  onTikTokPhotoStatusChange: (postId: string, data: { title?: string, description?: string }) => void
  onUpdateFBReshare: (postId: string, value: boolean) => void
  onToggleFBReshareAll: (value: boolean) => void
  onTweetTypeChange: (postId: string, type: TweetType) => void
  onApplyTweetTypeToAll: (type: TweetType) => void
  onPostTypeChange: (postId: string, network: PostDestinationType, postType: string) => void
  onCoverChange: (postId: string, url: string | undefined) => void
  onTikTokSettingsChange: (postId: string, settings: TikTokPostProfileSettings) => void
  onYoutubePrivacyChange: (postId: string, privacy: PrivacyStatus) => void
  onAiContentChange: (postId: string, value: boolean) => void
  onPinterestAiContentModeChange: (postId: string, mode?: PinterestAiContentMode) => void
  onAddComment: (postId: string, network: PostDestinationType) => void
  onMediaClick: (params: { url: string, type: 'image' | 'video', selector?: string }) => void
}

interface UseBulkPostHandlersParams {
  /** Current visible post ids in order. */
  orderRef: RefObject<string[]>
  /** All collaborators must be referentially stable — the handlers object is memoized on them. */
  reorderVisible: (nextVisibleIds: string[]) => void
  toggleSelected: (id: string) => void
  onPostRemoved: (id: string) => void
  onStatusEdited: (postId: string) => void
  onMediaClick: (params: { url: string, type: 'image' | 'video', selector?: string }) => void
}

/** One stable object with every per-post callback the bulk list hands down to its rows. */
export function useBulkPostHandlers(params: UseBulkPostHandlersParams): BulkPostHandlers {
  const dispatch = useDispatch<StoreThunkDispatch>()
  const { orderRef, reorderVisible, toggleSelected, onPostRemoved, onStatusEdited, onMediaClick } = params

  return useMemo(() => ({
    onMoveToTop: (id: string) => {
      const nextVisible = moveIdToTop(orderRef.current || [], id)
      if (nextVisible) {
        reorderVisible(nextVisible)
      }
    },
    onMoveToBottom: (id: string) => {
      const nextVisible = moveIdToBottom(orderRef.current || [], id)
      if (nextVisible) {
        reorderVisible(nextVisible)
      }
    },
    onRemove: (id: string) => {
      dispatch(removeMultiPosts({ ids: [id] }))
      onPostRemoved(id)
    },
    onToggleSelected: toggleSelected,
    onSelectedFBAlbumsChange: (postId, albums, applyToAll) => {
      dispatch(setMultiPostFBAlbums({ postId, albums, applyToAll }))
    },
    onSelectedPIBoardsChange: (postId, selection, applyToAll) => {
      dispatch(setMultiPostPiBoards({ postId, selection: selection as Record<string, PinterestBoard>, applyToAll }))
    },
    onSelectedIGLocationsChange: (postId, locations, applyToAll) => {
      dispatch(setMultiPostIGLocations({ postId, locations, applyToAll }))
    },
    onStatusChange: (postId, text, html, network) => {
      dispatch(editMultiPosts({ id: postId, network, data: { status: { text, html, touched: true } } }))
      onStatusEdited(postId)
    },
    onRecycleChange: (postId, value, network, applyToAll) => {
      if (applyToAll) {
        dispatch(setMultiPostsRecycle({ postIds: [postId], value, applyToAll: true }))
      } else {
        dispatch(editMultiPosts({ id: postId, network, data: {}, imageUrl: undefined, recycle: value }))
      }
    },
    onPinChange: (postId, data) => {
      dispatch(editMultiPosts({ id: postId, network: BRAND_PINTEREST, data }))
    },
    onYtStatusChange: (postId, data) => {
      dispatch(editMultiPosts({ id: postId, network: BRAND_YOUTUBE, data }))
    },
    onTikTokPhotoStatusChange: (postId, data) => {
      dispatch(editMultiPosts({ id: postId, network: BRAND_TIKTOK, data }))
    },
    onUpdateFBReshare: (postId, value) => {
      dispatch(editMultiPosts({
        id: postId,
        network: BRAND_FACEBOOK,
        data: { reshare: value } as Partial<ComposerFacebookPost>
      }))
    },
    onToggleFBReshareAll: (value) => {
      dispatch(setMultiPostFBReshareAll(value))
    },
    onTweetTypeChange: (postId, value) => {
      dispatch(editMultiPosts({ id: postId, network: BRAND_TWITTER, data: { tweetType: value } as Partial<ComposerTwitterPost> }))
    },
    onApplyTweetTypeToAll: (value) => {
      dispatch(setMultiPostTweetTypeAll(value))
    },
    onPostTypeChange: (postId, network, postType) => {
      dispatch(editMultiPosts({ id: postId, network, data: { postType } }))
    },
    onCoverChange: (postId, coverUrl) => {
      dispatch(editMultiPosts({ id: postId, network: BRAND_INSTAGRAM, data: { coverUrl } }))
    },
    onTikTokSettingsChange: (postId, settings) => {
      dispatch(setMultipostTikTokProfileSettings({ postId, settings }))
    },
    onYoutubePrivacyChange: (postId, privacy) => {
      dispatch(editMultiPosts({ id: postId, network: BRAND_YOUTUBE, data: { privacyStatus: privacy } }))
    },
    onAiContentChange: (postId, value) => {
      dispatch(setMultiPostAiContent({ postIds: [postId], value }))
    },
    onPinterestAiContentModeChange: (postId, mode) => {
      dispatch(editMultiPosts({
        id: postId,
        network: BRAND_PINTEREST,
        data: { aiContentMode: mode } as Partial<ComposerPinterestPost>
      }))
    },
    onAddComment: (postId) => {
      dispatch(addMultiPostComment({ postId }))
    },
    onMediaClick
  }), [dispatch, orderRef, reorderVisible, toggleSelected, onPostRemoved, onStatusEdited, onMediaClick])
}
